'use client';

import { toSortedCompat } from '@sweetistics/contracts/utils/sort';
import { ArrowUpDown, ArrowUpRight, ChevronDown, ChevronUp } from 'lucide-react';
import { useDeferredValue, useId, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { PackageRecord, ProjectScope, SummaryRecord } from './types';

const PROJECT_SCOPE_LABELS = new Map<ProjectScope, string>([
  ['desktop', 'Desktop'],
  ['mobile', 'Mobile'],
  ['web', 'Web'],
]);

const DEFAULT_LICENSE_LABEL = 'Declared';

const LICENSE_RESOLUTION_LABELS = new Map<PackageRecord['licenseResolution'], string>([
  ['declared', 'Declared'],
  ['dual-license', 'Dual license (permissive option applied)'],
  ['inferred', 'License inferred from embedded text'],
]);

const formatProjectLabel = (project: ProjectScope): string => PROJECT_SCOPE_LABELS.get(project) ?? project;

const formatLicenseResolutionLabel = (resolution: PackageRecord['licenseResolution']): string =>
  LICENSE_RESOLUTION_LABELS.get(resolution) ?? DEFAULT_LICENSE_LABEL;

interface OpenSourcePageClientProperties {
  packages: PackageRecord[];
  summary: SummaryRecord;
}

const PAGE_SIZE = 100;

export function OpenSourcePageClient({ packages, summary }: OpenSourcePageClientProperties) {
  const [searchInput, setSearchInput] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [sortKey, setSortKey] = useState<'name' | 'license'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const deferredSearch = useDeferredValue(searchInput);
  const searchQuery = useMemo(() => deferredSearch.trim().toLowerCase(), [deferredSearch]);

  const filteredPackages = useMemo(() => {
    if (!searchQuery) {
      return sortPackages(packages, sortKey, sortDirection);
    }

    const filtered = packages.filter((package_) => {
      const haystack = [
        package_.name,
        package_.version,
        package_.author ?? '',
        package_.normalizedLicense,
        package_.originalLicense,
        package_.description ?? '',
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(searchQuery);
    });

    return sortPackages(filtered, sortKey, sortDirection);
  }, [packages, searchQuery, sortDirection, sortKey]);

  const visiblePackages = useMemo(() => filteredPackages.slice(0, visibleCount), [filteredPackages, visibleCount]);
  const hasMore = visibleCount < filteredPackages.length;

  const generatedAt = useMemo(() => {
    if (!summary.generatedAt) {
      return null;
    }

    const parsed = new Date(summary.generatedAt);
    if (Number.isNaN(parsed.getTime())) {
      return summary.generatedAt;
    }

    try {
      return new Intl.DateTimeFormat('en-US', { dateStyle: 'long', timeStyle: 'short' }).format(parsed);
    } catch {
      return parsed.toLocaleString('en-US');
    }
  }, [summary.generatedAt]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    setVisibleCount(PAGE_SIZE);
  };

  const handleSortChange = (key: 'name' | 'license') => {
    setVisibleCount(PAGE_SIZE);
    setSortKey((previousKey) => {
      if (previousKey === key) {
        setSortDirection((previousDirection) => (previousDirection === 'asc' ? 'desc' : 'asc'));
        return previousKey;
      }

      setSortDirection('asc');
      return key;
    });
  };

  return (
    <>
      <HeroSection totalPackages={summary.totalPackages} generatedAt={generatedAt} />
      <LicenseTable
        packages={visiblePackages}
        totalMatches={filteredPackages.length}
        hasMore={hasMore}
        onLoadMore={() => {
          setVisibleCount((previous) => Math.min(previous + PAGE_SIZE, filteredPackages.length));
        }}
        searchInput={searchInput}
        onSearchChange={handleSearchChange}
        sortKey={sortKey}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
      />
      <GratitudeSection />
    </>
  );
}

function HeroSection({ totalPackages, generatedAt }: { totalPackages: number; generatedAt: string | null }) {
  return (
    <section className="bg-muted/40 px-4 py-16">
      <div className="mx-auto flex max-w-5xl flex-col items-center gap-6 text-center">
        <Badge variant="outline">Open Source</Badge>
        <div className="space-y-4">
          <h1 className="text-4xl font-semibold tracking-tight sm:text-5xl">Sweetistics Open Source Credits</h1>
          <p className="text-base text-muted-foreground sm:text-lg">
            We build on {totalPackages} community packages maintained under permissive and attribution-friendly
            licenses. Browse every dependency, confirm the license we rely on, and follow links back to the original
            projects.
          </p>
        </div>
        {generatedAt ? (
          <p className="text-xs text-muted-foreground">
            Snapshot generated on <span className="font-medium text-foreground">{generatedAt}</span> via{' '}
            <code className="rounded bg-muted px-1.5 py-0.5 text-xs">pnpm run licenses:generate</code>.
          </p>
        ) : null}
      </div>
    </section>
  );
}

function LicenseTable({
  packages,
  totalMatches,
  hasMore,
  onLoadMore,
  searchInput,
  onSearchChange,
  sortKey,
  sortDirection,
  onSortChange,
}: {
  packages: PackageRecord[];
  totalMatches: number;
  hasMore: boolean;
  onLoadMore: () => void;
  searchInput: string;
  onSearchChange: (value: string) => void;
  sortKey: 'name' | 'license';
  sortDirection: 'asc' | 'desc';
  onSortChange: (key: 'name' | 'license') => void;
}) {
  const searchInputId = useId();

  return (
    <section className="px-4 pb-16">
      <div className="mx-auto flex max-w-6xl flex-col gap-4">
        <label htmlFor={searchInputId} className="sr-only">
          Filter packages by name, author, or license
        </label>
        <Input
          id={searchInputId}
          value={searchInput}
          onChange={(event) => {
            onSearchChange(event.target.value);
          }}
          placeholder="Filter by name, author, or license"
          className="bg-card"
        />
        <div className="rounded-lg border border-border/60">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[36%]">
                  <SortButton
                    label="Package"
                    active={sortKey === 'name'}
                    direction={sortDirection}
                    onClick={() => {
                      onSortChange('name');
                    }}
                  />
                </TableHead>
                <TableHead className="w-[28%] whitespace-normal">
                  <SortButton
                    label="License"
                    active={sortKey === 'license'}
                    direction={sortDirection}
                    onClick={() => {
                      onSortChange('license');
                    }}
                  />
                </TableHead>
                <TableHead className="w-[20%] whitespace-normal">Used In</TableHead>
                <TableHead className="w-[16%] whitespace-normal">Author</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="py-12 text-center text-sm text-muted-foreground">
                    No packages match the current filters.
                  </TableCell>
                </TableRow>
              ) : (
                packages.map((package_) => (
                  <TableRow key={`${package_.name}@${package_.version}`}>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <a
                          href={package_.homepage}
                          className="flex items-center gap-1 text-sm font-medium text-foreground hover:text-primary"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {package_.name}
                          <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
                        </a>
                        {package_.description ? (
                          <p className="text-xs text-muted-foreground break-words leading-relaxed">
                            {package_.description}
                          </p>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="align-top whitespace-normal">
                      <div className="flex flex-col gap-1">
                        <Badge variant="secondary" className="w-fit">
                          {package_.normalizedLicense}
                        </Badge>
                        {package_.normalizedLicense === package_.originalLicense ? null : (
                          <p className="text-xs text-muted-foreground break-words">
                            Declared: {package_.originalLicense}
                          </p>
                        )}
                        {package_.licenseResolution === 'declared' ? null : (
                          <p className="text-xs text-muted-foreground">
                            {formatLicenseResolutionLabel(package_.licenseResolution)}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">Ecosystem: {package_.ecosystem}</p>
                      </div>
                    </TableCell>
                    <TableCell className="align-top text-sm">
                      <ProjectBadges projects={package_.projects} />
                    </TableCell>
                    <TableCell className="align-top text-sm">
                      {package_.author || <span className="text-muted-foreground">—</span>}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-sm text-muted-foreground">
            Showing {packages.length} of {totalMatches} package{totalMatches === 1 ? '' : 's'}.
          </span>
          {hasMore ? (
            <Button onClick={onLoadMore} variant="outline" className="w-full sm:w-auto">
              Load {Math.min(PAGE_SIZE, totalMatches - packages.length)} more
            </Button>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function ProjectBadges({ projects }: { projects: ProjectScope[] }) {
  if (projects.length === 0) {
    return <span className="text-muted-foreground">—</span>;
  }

  return (
    <div className="flex flex-wrap gap-1">
      {projects.map((project) => (
        <Badge key={project} variant="outline" className="text-xs font-medium">
          {formatProjectLabel(project)}
        </Badge>
      ))}
    </div>
  );
}

function SortButton({
  label,
  active,
  direction,
  onClick,
}: {
  label: string;
  active: boolean;
  direction: 'asc' | 'desc';
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground transition-colors hover:text-foreground"
    >
      {label}
      {(() => {
        if (!active) return <ArrowUpDown className="h-3 w-3" aria-hidden />;
        return direction === 'asc' ? (
          <ChevronUp className="h-3 w-3" aria-hidden />
        ) : (
          <ChevronDown className="h-3 w-3" aria-hidden />
        );
      })()}
      <span className="sr-only">Sort by {label}</span>
    </button>
  );
}

const NAME_COLLATOR = new Intl.Collator('en', { sensitivity: 'base' });

const comparePackagesByName = (a: PackageRecord, b: PackageRecord) => {
  const primary = NAME_COLLATOR.compare(a.name, b.name);
  if (primary !== 0) {
    return primary;
  }
  return NAME_COLLATOR.compare(a.normalizedLicense, b.normalizedLicense);
};

const comparePackagesByLicense = (a: PackageRecord, b: PackageRecord) => {
  const primary = NAME_COLLATOR.compare(a.normalizedLicense, b.normalizedLicense);
  if (primary !== 0) {
    return primary;
  }
  return NAME_COLLATOR.compare(a.name, b.name);
};

function sortPackages(packages: PackageRecord[], key: 'name' | 'license', direction: 'asc' | 'desc'): PackageRecord[] {
  const comparator = key === 'name' ? comparePackagesByName : comparePackagesByLicense;
  const multiplier = direction === 'asc' ? 1 : -1;
  return toSortedCompat(packages, (a, b) => comparator(a, b) * multiplier);
}

function GratitudeSection() {
  return (
    <section className="px-4 pb-16">
      <div className="mx-auto max-w-4xl">
        <Card className="border border-border/60 bg-card">
          <CardHeader>
            <CardTitle>Thank You, Maintainers</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p>
              Sweetistics simply would not exist without the thousands of maintainers who share their craft with the
              world. Every feature we ship stands on their shoulders—documentation patiently written, edge cases ironed
              out, nightly builds published in spare hours.
            </p>
            <p>
              To everyone who fixes bugs after work, moderates forums, triages issues, and keeps these projects healthy:
              thank you. We are deeply grateful for your generosity and we&apos;re committed to honoring your work with
              clear attribution and continued support.
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
